import { useState } from "react";
import { useTranslation } from "../../lib";
import { QuestionnaireDraftRecovery } from "../../lib/custom_hooks/useQuestionnaireDraft";
import { useSelectTheme } from "../../store";
import { Button, ButtonText } from "..";

export type QuestionnaireDraftRestoreChoice = Exclude<
  QuestionnaireDraftRecovery,
  { state: "none" } | { state: "checking" }
>;

export interface QuestionnaireDraftRestorePopProps {
  recovery: QuestionnaireDraftRestoreChoice;
  onRestore: () => void;
  onDiscard: () => void;
  onStartFresh: () => void;
  onRetryCheck: () => void;
  // closes the editor; the recovery copy stays as it is
  onCancel: () => void;
  // the editor cannot change this questionnaire's questions or display logic: an existing task on a
  // live project, where a restore applies the rest of the copy only
  questionsLocked?: boolean;
}

const formatSavedAt = (savedAt: string) => new Date(savedAt).toLocaleString();

/**
 * What the researcher decides about a recovery copy as a questionnaire editor opens. Nothing the editor
 * holds is saved until they do, so the copy is never replaced behind their back: restoring it, or
 * discarding it, is always their choice, and closing the editor keeps it.
 */
export const QuestionnaireDraftRestorePop = (props: QuestionnaireDraftRestorePopProps) => {
  const {
    recovery,
    onRestore,
    onDiscard,
    onStartFresh,
    onRetryCheck,
    onCancel,
    questionsLocked = false,
  } = props;
  const { t } = useTranslation();
  const { color, pad, fontSize, fontWeight, borderRadius } = useSelectTheme();
  const [reviewed, setReviewed] = useState(false);

  // React escapes the text it renders, so i18next must not escape the date's slashes as well
  const savedAtMessage = (savedAt: string | null, key: string) =>
    savedAt
      ? t(key, { time: formatSavedAt(savedAt), interpolation: { escapeValue: false } })
      : t(`${key}_unsent`);

  let title = "";
  let message = "";
  let body: JSX.Element | null = null;
  // said only when the copy holds question or display-logic edits that the restore will leave out
  const lockedNote = (
    <p id="questionnaire_draft_restore_locked" css={{ margin: `0 0 ${pad.large}` }}>
      {t("questionnaire_draft.locked_questions_note")}
    </p>
  );
  const leavesQuestionsOut =
    questionsLocked &&
    (recovery.state === "offer" ||
      (recovery.state === "conflict" && recovery.reason === "changed")) &&
    recovery.questionsChanged;
  let actions: JSX.Element;
  const cancel = (
    <ButtonText onClick={onCancel}>{t("questionnaire_draft.cancel_button")}</ButtonText>
  );
  const discard = (
    <ButtonText onClick={onDiscard}>{t("questionnaire_draft.discard_button")}</ButtonText>
  );

  switch (recovery.state) {
    case "offer":
      title = t("questionnaire_draft.offer_title");
      message = savedAtMessage(recovery.savedAt, "questionnaire_draft.offer_message");
      if (leavesQuestionsOut) body = lockedNote;
      actions = (
        <>
          {discard}
          <Button type="button" autoFocus onClick={onRestore}>
            {t("questionnaire_draft.restore_button")}
          </Button>
        </>
      );
      break;
    case "conflict":
      if (recovery.reason === "missing") {
        title = t("questionnaire_draft.missing_title");
        message = savedAtMessage(recovery.savedAt, "questionnaire_draft.missing_message");
        actions = (
          <>
            {cancel}
            {discard}
          </>
        );
        break;
      }
      title = t("questionnaire_draft.conflict_title");
      message = savedAtMessage(recovery.savedAt, "questionnaire_draft.conflict_message");
      if (leavesQuestionsOut) body = lockedNote;
      actions = (
        <>
          {cancel}
          {discard}
          <Button type="button" onClick={onRestore}>
            {t("questionnaire_draft.restore_anyway_button")}
          </Button>
        </>
      );
      break;
    case "review_saved_work":
      title = t("questionnaire_draft.review_title");
      if (recovery.savedList === null) {
        message = t("questionnaire_draft.review_load_failed");
        actions = (
          <>
            {cancel}
            <Button type="button" onClick={onRetryCheck}>
              {t("questionnaire_draft.retry_button")}
            </Button>
          </>
        );
        break;
      }
      message = t("questionnaire_draft.review_message");
      body = (
        <>
          <ul
            data-testid="questionnaire_draft_saved_list"
            css={{ margin: `0 0 ${pad.large}`, paddingLeft: pad.xl }}
          >
            {recovery.savedList.map(({ taskId, name, updatedAt }) => (
              <li key={taskId}>
                {name} — {formatSavedAt(updatedAt)}
              </li>
            ))}
          </ul>
          <label
            css={{ display: "flex", alignItems: "center", gap: pad.small, marginBottom: pad.large }}
          >
            <input
              type="checkbox"
              checked={reviewed}
              onChange={(event) => setReviewed(event.target.checked)}
            />
            {t("questionnaire_draft.review_confirm")}
          </label>
        </>
      );
      actions = (
        <>
          {cancel}
          <Button type="button" disabled={!reviewed} onClick={onRestore}>
            {t("questionnaire_draft.restore_button")}
          </Button>
        </>
      );
      break;
    case "unrestorable":
      title = t(`questionnaire_draft.${recovery.reason}_title`);
      message = t(`questionnaire_draft.${recovery.reason}_message`);
      actions = (
        <>
          {cancel}
          {discard}
        </>
      );
      break;
    case "check_failed":
      title = t("questionnaire_draft.check_failed_title");
      message = t("questionnaire_draft.check_failed_message");
      actions = (
        <>
          <ButtonText onClick={onStartFresh}>
            {t("questionnaire_draft.start_fresh_button")}
          </ButtonText>
          <Button type="button" autoFocus onClick={onRetryCheck}>
            {t("questionnaire_draft.retry_button")}
          </Button>
        </>
      );
      break;
  }

  return (
    // the same layer as the editor's own confirm popups, mounted after the editor so it sits above it
    <div
      data-testid="questionnaire_draft_restore_backdrop"
      css={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 99999,
        background: "#00000040",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="questionnaire_draft_restore_title"
        aria-describedby={
          leavesQuestionsOut
            ? "questionnaire_draft_restore_message questionnaire_draft_restore_locked"
            : "questionnaire_draft_restore_message"
        }
        css={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxSizing: "border-box",
          width: "560px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          padding: pad.xl,
          borderRadius: borderRadius.default,
          background: color.white,
          color: color.textIcon.title,
        }}
      >
        <h2
          id="questionnaire_draft_restore_title"
          css={{ margin: 0, fontSize: fontSize.h3, fontWeight: fontWeight.bold }}
        >
          {title}
        </h2>
        <p id="questionnaire_draft_restore_message" css={{ margin: `${pad.large} 0` }}>
          {message}
        </p>
        {body}
        <div
          css={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: pad.large,
          }}
        >
          {actions}
        </div>
      </div>
    </div>
  );
};
